import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import BatchSelectionModal from './BatchSelectionModal';
import ManualMedicineEntry from './ManualMedicineEntry';
import MedicineListItem from './MedicineListItem';
import { Modal } from './Modal';
import {API_CONFIG, getHeaders, PHARMACY_IDS} from '../../config/api';
import B2BPartnerSection from './B2BPartnerSection';
import AdditionalChargesSection from './AdditionalChargesSection';
import OrderSummary from './OrderSummary';
import PharmacySelector from './PharmacySelector';
import { useToast } from '../../context/ToastContext';
import { calculateTotal } from '../../utils/calculations';
import { channelService, formatWalletBalanceMinor } from '../../services/channelService';
import CustomerAddressSection from './CustomerAddressSection';
import { internalApi, getApiErrorMessage, useApiSessionRestored } from '../../services/internalApi';

function DraftEditor({draft, onSave, onCancel, onBill, onAddShortbook}) {
    const toast = useToast();
    const apiSessionRestored = useApiSessionRestored();
    const [editedDraft, setEditedDraft] = useState({
        ...draft,
        items: draft.items || [],
        additionalCharges: draft.additionalCharges || [],
        customer: draft.customer || {
            name: '',
            mobile: '',
            address_details: {
                address: '',
                pincode: '',
                latitude: '',
                longitude: ''
            }
        },
        pharmacy: draft.pharmacy || '',
        doctor: draft.doctor || '',
        partner: draft.partner || '',
        customPartner: draft.customPartner || draft.custom_partner || '',
        pocName: draft.pocName || draft.poc_name || '',
        status: draft.status || 'pending',
        notes: draft.notes || ''
    });
    const [newCharge, setNewCharge] = useState({type: 'SHIPPING_CHARGE', name: '', amount: 0});
    const setNewItem = useState({medicineId: '', quantity: 1})[1];
    const [medicineSearch, setMedicineSearch] = useState('');
    const setShowMedicineDropdown = useState(false)[1];
    const [searchResults, setSearchResults] = useState([]);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [batches, setBatches] = useState([]);
    const [medicine, setMedicine] = useState([]);
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [showManualEntryModal, setShowManualEntryModal] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(draft.partner || 'IHS_API');
    const [selectedPharmacy, setSelectedPharmacy] = useState(
        Object.keys(PHARMACY_IDS).find(key => PHARMACY_IDS[key] === draft.pharmacy) || ''
    );
    const [pocName, setPocName] = useState(draft.pocName || draft.poc_name || '');
    const [doctor, setDoctor] = useState(draft.doctor || '');
    const [, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tempNotes, setTempNotes] = useState(draft.notes || '');
    const [showNotesSection, setShowNotesSection] = useState(false);
    const [redeemWallet, setRedeemWallet] = useState(false);
    const [walletBalanceMinor, setWalletBalanceMinor] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);
    const prevCanRedeemRef = useRef(false);
    const [shortbookModalOpen, setShortbookModalOpen] = useState(false);
    const [shortbookStrip, setShortbookStrip] = useState('');
    const [shortbookSubmitting, setShortbookSubmitting] = useState(false);

    // Predefined charge types
    const chargeTypes = [
        { id: 'SHIPPING_CHARGE', label: 'Delivery Charge' },
        { id: 'LATE_NIGHT_FEE', label: 'Late Night Delivery Charge' },
        { id: 'RETURN_CHARGES', label: 'Return Charges' },
        { id: 'COD_CHARGE', label: 'COD Charge' },
        { id: 'custom', label: 'Custom Charge' }
    ];

    const orderAmountMinor = useMemo(() => {
        const rupees = parseFloat(calculateTotal(editedDraft.items, editedDraft.additionalCharges)) || 0;
        return Math.round(rupees * 100);
    }, [editedDraft.items, editedDraft.additionalCharges]);

    const walletRedeemAllowed = useMemo(() => {
        if (!selectedPartner || selectedPartner === 'CUSTOM') return false;
        if (walletLoading) return false;
        if (walletBalanceMinor == null || Number.isNaN(Number(walletBalanceMinor))) return false;
        if (orderAmountMinor <= 0) return false;
        return Number(walletBalanceMinor) >= orderAmountMinor;
    }, [selectedPartner, walletLoading, walletBalanceMinor, orderAmountMinor]);

    const redeemWalletStatusText = useMemo(() => {
        if (!selectedPartner || selectedPartner === 'CUSTOM') return null;
        if (walletLoading) return 'Checking partner wallet balance…';
        if (orderAmountMinor <= 0) return 'Add items so the order total can be compared with the wallet.';
        if (walletBalanceMinor == null) return 'Could not load wallet balance.';
        if (Number(walletBalanceMinor) < orderAmountMinor) {
            return `Available ${formatWalletBalanceMinor(walletBalanceMinor)} is less than this order total (${formatWalletBalanceMinor(orderAmountMinor)}). Redeem is turned off.`;
        }
        return null;
    }, [selectedPartner, walletLoading, orderAmountMinor, walletBalanceMinor]);

    useEffect(() => {
        if (!selectedPartner || selectedPartner === 'CUSTOM') {
            setWalletBalanceMinor(null);
            setWalletLoading(false);
            return;
        }
        let cancelled = false;
        setWalletLoading(true);
        channelService
            .getChannelWallet(selectedPartner)
            .then((data) => {
                if (!cancelled) {
                    const b = data?.balance;
                    setWalletBalanceMinor(b == null ? null : Number(b));
                }
            })
            .catch(() => {
                if (!cancelled) setWalletBalanceMinor(null);
            })
            .finally(() => {
                if (!cancelled) setWalletLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedPartner, apiSessionRestored]);

    useEffect(() => {
        if (!walletRedeemAllowed) {
            setRedeemWallet(false);
            prevCanRedeemRef.current = false;
            return;
        }
        if (!prevCanRedeemRef.current) {
            setRedeemWallet(true);
        }
        prevCanRedeemRef.current = true;
    }, [walletRedeemAllowed]);

    const debounce = (func, delay) => {
        let timeoutId;
        return function(...args) {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    };

    const debouncedSearch = useCallback((searchText) => {
        if (searchText.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        setIsLoading(true);
        internalApi.post(
            API_CONFIG.ENDPOINTS.MEDICINE_SEARCH,
            {searchstring: searchText},
            {headers: getHeaders(PHARMACY_IDS[selectedPharmacy], selectedPartner)}
        )
        .then(response => {
            // Only set results if the search text is still >= 3 characters
            if (medicineSearch.length >= 3) {
                setSearchResults(response.data.data.result); 
            }
            setIsSearching(false);
            setIsLoading(false);
        })
        .catch(error => {
            console.error('Error searching medicines:', error);
            setIsSearching(false);
            setIsLoading(false);
        });
    }, [selectedPharmacy, selectedPartner, medicineSearch]);

    // Create a debounced version of search
    const debouncedSearchRef = useRef();
    const timeoutRef = useRef();
    
    useEffect(() => {
        debouncedSearchRef.current = debounce((searchText) => {
            debouncedSearch(searchText);
        }, 500);
    }, [debouncedSearch]);

    useEffect(() => {
        // Clear any pending timeout when search text changes
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (medicineSearch.length >= 3) {
            timeoutRef.current = setTimeout(() => {
                debouncedSearchRef.current(medicineSearch);
            }, 500);
        } else {
            setSearchResults([]);
            setIsSearching(false);
            setIsLoading(false);
        }

        // Cleanup timeout on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [medicineSearch]);


    const fetchBatches = async (medicine) => {
        try {
            setIsLoading(true);
            const response = await internalApi.post(
                API_CONFIG.ENDPOINTS.MEDICINE_BATCH,
                {medicine_id: medicine.medicine_id},
                {headers: getHeaders(PHARMACY_IDS[selectedPharmacy], selectedPartner)}
            );
            setBatches(response.data.data);
            setMedicine(medicine);
            setShowBatchModal(true);
            setSearchResults([]);
            setShowMedicineDropdown(false);
        } catch (error) {
            console.error('Error fetching batches:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMedicineSelect = async (medicine) => {
        setSelectedMedicine({
            ...medicine,
            packing_size: medicine.packing_size
        });
        setMedicineSearch(medicine.medicine_name);
        setShowMedicineDropdown(false);
        setSearchResults([]);
        await fetchBatches(medicine);
    };

    const handleBatchSelect = (batch) => {
        if (selectedMedicine) {
            const isDuplicate = editedDraft.items.some(item =>
                item.medicine.id.toString() === selectedMedicine.medicine_id.toString() &&
                item.medicine.batch === batch.batch
            );

            if (isDuplicate) {
                toast.info('This medicine with the same batch is already in the draft.');
                setShowBatchModal(false);
                return;
            }
        }

        // Generate a truly unique ID
        const uniqueId = selectedMedicine
            ? selectedMedicine.medicine_id
            : `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const newMedicine = {
            id: uniqueId,
            name: selectedMedicine ? selectedMedicine.medicine_name : batch.medicine_name,
            manufacturer: selectedMedicine ? selectedMedicine.manufacturer_name : batch.manufacturer_name,
            content: selectedMedicine ? selectedMedicine.content : batch.content || '',
            size: selectedMedicine ? selectedMedicine.size : batch.content || 1,
            price: batch.mrp,
            price_to_retailer: batch.price_to_retailer || 0,
            batch: batch.batch,
            expiry: batch.expiry,
            stock: selectedMedicine ? batch.stock : '-',
            packing_size: selectedMedicine ? selectedMedicine.packing_size : batch.packing_size || '',
            discount_tier: batch.discount_tier || selectedMedicine?.discount_tier || ''
        };

        setEditedDraft(prevDraft => ({
            ...prevDraft,
            items: [
                ...prevDraft.items,
                {
                    medicine: newMedicine,
                    quantity: batch.quantity || 1,
                    discount: 0
                }
            ]
        }));

        setNewItem({medicineId: '', quantity: 1});
        setMedicineSearch('');
        setSelectedMedicine(null);
        setShowBatchModal(false);
    };

    const updateItemQuantity = (medicineId, batch, newQuantity) => {
        console.log('Updating quantity for medicine:', medicineId, 'batch:', batch, 'to:', newQuantity); // Debug log
        setEditedDraft(prev => ({
            ...prev,
            items: prev.items.map(item => {
                // Update only if both medicine ID and batch match exactly
                return item.medicine.id.toString() === medicineId.toString() && 
                       item.medicine.batch === batch
                    ? {...item, quantity: parseInt(newQuantity) || 0}
                    : item;
            })
        }));
    };

    const updateItemDiscount = (itemId, newDiscount) => {
        console.log('Updating discount for item:', itemId, 'to:', newDiscount); // Debug log
        setEditedDraft(prev => ({
            ...prev,
            items: prev.items.map(item => {
                const currentItemId = item.medicine.id.toString();
                const targetItemId = itemId.toString();

                console.log('Comparing:', currentItemId, targetItemId); // Debug log

                // Update only the matching item
                return currentItemId === targetItemId
                    ? {...item, discount: parseFloat(newDiscount) || 0}
                    : item;
            })
        }));
    };

    const addCharge = () => {
        if (newCharge.type === 'custom' && !newCharge.name) {
            toast.error('Please enter a name for the custom charge.');
            return;
        }

        const chargeName = newCharge.type === 'custom'
            ? newCharge.name
            : chargeTypes.find(t => t.id === newCharge.type)?.label;

        setEditedDraft({
            ...editedDraft,
            additionalCharges: [
                ...editedDraft.additionalCharges,
                {
                    id: Date.now(),
                    type: newCharge.type,
                    name: chargeName,
                    amount: parseFloat(newCharge.amount) || 0
                }
            ]
        });
        setNewCharge({type: 'SHIPPING_CHARGE', name: '', amount: 0});
    };

    const removeCharge = (chargeId) => {
        setEditedDraft({
            ...editedDraft,
            additionalCharges: editedDraft.additionalCharges.filter(
                charge => charge.id !== chargeId
            )
        });
    };

    const updateCharge = (chargeId, amount) => {
        setEditedDraft({
            ...editedDraft,
            additionalCharges: editedDraft.additionalCharges.map(charge =>
                charge.id === chargeId
                    ? {...charge, amount: parseFloat(amount) || 0}
                    : charge
            )
        });
    };

    const calculateItemPrice = (item) => {
        const basePrice = item.medicine.price * item.quantity;
        const discountAmount = basePrice * (item.discount / 100);
        return (basePrice - discountAmount);
    };


    const removeItem = (medicineId) => {
        // Create a new array with filtered items
        const updatedItems = editedDraft.items.filter(item =>
            // Convert both IDs to strings for comparison
            item.medicine.id.toString() !== medicineId.toString()
        );

        // Set the entire editedDraft with the new items array
        setEditedDraft(prevDraft => ({
            ...prevDraft,
            items: updatedItems
        }));
    };

    const updateCustomerField = (field, value) => {
        setEditedDraft({
            ...editedDraft,
            customer: {
                ...editedDraft.customer,
                [field]: value
            }
        });
    };

    const handleManualMedicineEntry = () => {
        setShowManualEntryModal(true);
        setShowMedicineDropdown(false);
        setSearchResults([]);
        setMedicineSearch('');
    };

    const handleManualMedicineSave = (manualMedicine) => {
        const batch = {
            id: `custom-batch-${Date.now()}`,
            medicine_name: manualMedicine.medicine_name,
            manufacturer_name: manualMedicine.manufacturer_name,
            content: manualMedicine.content || '',
            size: manualMedicine.size || 1,
            mrp: manualMedicine.mrp,
            price_to_retailer: 0, // Default to 0 for custom medicines
            quantity: manualMedicine.quantity,
            batch: manualMedicine.batch,
            expiry: manualMedicine.expiry,
            expiry_label: manualMedicine.expiry_label,
            packing_size: manualMedicine.packing_size || '',
            discount_tier: manualMedicine.discount_tier || ''
        };

        handleBatchSelect(batch);
        setShowManualEntryModal(false);
    };

    const handlePharmacyChange = (pharmacy) => {
        setSelectedPharmacy(pharmacy);
        setEditedDraft(prev => ({
            ...prev,
            pharmacy: PHARMACY_IDS[pharmacy],
            orderId: PHARMACY_IDS[pharmacy]
        }));
    };

    const handleSave = async () => {
        try {
            if (!editedDraft.pharmacy) {
                toast.error('Please select a pharmacy.');
                return false;
            }

            if (!editedDraft.items || editedDraft.items.length === 0) {
                toast.error('Please add at least one item to the draft.');
                return false;
            }

            const invalidItems = editedDraft.items.filter(item => !item.medicine || !item.medicine.id);
            if (invalidItems.length > 0) {
                console.error('Invalid items found:', invalidItems);
                toast.error('There are invalid items in your draft. Please remove them and try again.');
                return false;
            }

            editedDraft.notes = tempNotes;

            setIsSaving(true);
            setIsLoading(true);
            await onSave(editedDraft);
            setIsSaving(false);
            setIsLoading(false);
            return true;
        } catch (error) {
            console.error('Error saving draft:', error);
            const msg = getApiErrorMessage(error, 'Failed to save draft.');
            toast.error(msg);
            setIsSaving(false);
            setIsLoading(false);
            return false;
        }
    };

    // Update draft with notes when they change
    useEffect(() => {
        setEditedDraft(prev => ({
            ...prev,
            notes: tempNotes
        }));
    }, [tempNotes]);

    const clearNotes = () => {
        setTempNotes('');
    };

    return (
        <div className="card-surface mt-6 p-6 sm:p-8">
            <div className="mb-8 flex flex-col gap-4 border-b border-slate-100 pb-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">Edit draft</p>
                    <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Order &amp; items</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                        {draft.order_id ? `CE ${draft.order_id}` : 'No order yet'}
                    </span>
                </div>
            </div>

            <PharmacySelector
                selectedPharmacy={selectedPharmacy}
                onPharmacyChange={handlePharmacyChange}
            />

            <B2BPartnerSection
                selectedPartner={selectedPartner}
                onPartnerChange={(value) => {
                    setSelectedPartner(value);
                    setEditedDraft(prev => ({
                        ...prev,
                        partner: value
                    }));
                }}
                customPartner={editedDraft.customPartner}
                onCustomPartnerChange={(value) => setEditedDraft(prev => ({
                    ...prev,
                    customPartner: value,
                    partner: value
                }))}
                pocName={pocName}
                onPocNameChange={(value) => {
                    setPocName(value);
                    setEditedDraft(prev => ({
                        ...prev,
                        pocName: value
                    }));
                }}
                draft={editedDraft}
                onDraftUpdate={(updatedDraft) => {
                    setEditedDraft(updatedDraft);
                }}
            />

            <div className="mb-6 rounded-xl border border-slate-100 bg-slate-50/80 p-4 sm:p-5">
                <h3 className="mb-4 text-sm font-semibold text-slate-800">Customer</h3>
                <div>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                Name
                            </label>
                            <input
                                type="text"
                                className="input-field"
                                value={editedDraft.customer.name}
                                onChange={(e) => updateCustomerField('name', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                Doctor
                            </label>
                            <input
                                type="text"
                                className="input-field"
                                value={doctor}
                                onChange={(e) => {
                                    setDoctor(e.target.value);
                                    setEditedDraft(prev => ({
                                        ...prev,
                                        doctor: e.target.value
                                    }));
                                }}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                Mobile
                            </label>
                            <input
                                type="text"
                                className="input-field"
                                value={editedDraft.customer.mobile}
                                onChange={(e) => updateCustomerField('mobile', e.target.value)}
                            />
                        </div>
                    </div>

                    <CustomerAddressSection
                        addressDetails={editedDraft.customer.address_details}
                        onChange={(address_details) => {
                            setEditedDraft((prev) => ({
                                ...prev,
                                customer: { ...prev.customer, address_details },
                            }));
                        }}
                    />
                </div>
            </div>

             {/* Collapsible Temporary Notes Section */}
             <div className="mb-6 rounded-xl border border-slate-100 bg-white p-4">
                <button 
                    className="flex w-full items-center justify-between text-left"
                    onClick={() => setShowNotesSection(!showNotesSection)}
                >
                    <h3 className="text-lg font-semibold text-slate-800">Notes</h3>
                    <i className="material-icons-outlined text-gray-600">
                        {showNotesSection ? 'expand_less' : 'expand_more'}
                    </i>
                </button>
                
                {showNotesSection && (
                    <div className="mt-2">
                        <textarea
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Write your notes here..."
                            rows="2"
                            value={tempNotes}
                            onChange={(e) => setTempNotes(e.target.value)}
                        ></textarea>
                        <div className="flex justify-end mt-2">
                            <button 
                                onClick={clearNotes}
                                className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200 transition"
                            >
                                Clear Notes
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add New Item Section with Search */}
            <div className="mb-6 rounded-xl border border-slate-100 bg-slate-50/80 p-4 sm:p-5">
                <h3 className="mb-4 text-lg font-semibold text-slate-800">Add item</h3>
                <div className="flex gap-4 items-end">
                    <div className="flex-1 relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Search Medicine
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                className="input-field pr-10"
                                value={medicineSearch}
                                onChange={(e) => {
                                    setMedicineSearch(e.target.value);
                                }}
                                placeholder="Search medicines..."
                            />
                            {isSearching && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div
                                        className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                                </div>
                            )}
                        </div>

                        {searchResults.length > 0 && (
                            <div
                                className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                                {searchResults.map(medicine => (
                                    <button
                                        key={medicine.medicine_id}
                                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex justify-between items-center"
                                        onClick={() => handleMedicineSelect(medicine)}
                                    >
                                        <div>
                                            {medicine.strip_quantity ? '' : <div className="font-medium text-red-500">No stock</div>}
                                            <div className="font-medium text-gray-800">{medicine.medicine_name}</div>
                                            <div className="text-sm text-gray-600">
                                                {medicine.manufacturer_name} • {medicine.packing_size}
                                            </div>
                                            {medicine.content && (
                                                <div className="text-sm text-gray-600">
                                                    Salt: {medicine.content}
                                                </div>
                                            )}
                                            {medicine.size && (
                                                <div className="text-sm text-gray-600">
                                                   stock: {medicine.strip_quantity}({medicine.loose_quantity})
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            MRP: ₹{medicine.mrp}
                                        </div>
                                    </button>
                                ))}
                                <button
                                    className="w-full px-4 py-3 text-left text-primary-600 hover:bg-gray-100 border-t font-medium"
                                    onClick={handleManualMedicineEntry}
                                >
                                    + Add Custom Medicine
                                </button>
                            </div>
                        )}

                        {/* Show Add Custom Medicine button when search has been performed with no results */}
                        {medicineSearch.length >= 3 && searchResults.length === 0 && !isSearching && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                                <div className="px-4 py-2 text-gray-500">No results found</div>
                                <button
                                    className="w-full px-4 py-3 text-left text-primary-600 hover:bg-gray-100 border-t font-medium"
                                    onClick={handleManualMedicineEntry}
                                >
                                    + Add Custom Medicine
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Items List */}
            <div className="mt-6 space-y-4">
                {editedDraft.items.map((item, index) => (
                    <MedicineListItem
                        key={`${item.medicine.id}-${index}`}
                        item={item}
                        onQuantityChange={(medicineId, batch, quantity) => updateItemQuantity(medicineId, batch, quantity)}
                        onDiscountChange={(discount) => updateItemDiscount(item.medicine.id, discount)}
                        onRemove={removeItem}
                        calculateItemPrice={calculateItemPrice}
                    />
                ))}
            </div>

            <AdditionalChargesSection
                charges={editedDraft.additionalCharges}
                newCharge={newCharge}
                onNewChargeChange={setNewCharge}
                onAddCharge={addCharge}
                onUpdateCharge={updateCharge}
                onRemoveCharge={removeCharge}
            />

            <OrderSummary
                items={editedDraft.items}
                additionalCharges={editedDraft.additionalCharges}
            />

            <div className="mt-6 rounded-xl border border-amber-100 bg-gradient-to-r from-amber-50/50 to-white px-4 py-4 sm:px-5">
                <label
                    className={`flex items-start gap-3 ${walletRedeemAllowed ? 'cursor-pointer' : 'cursor-default'}`}
                >
                    <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-amber-300 text-primary-600 focus:ring-primary-500/40 disabled:cursor-not-allowed disabled:opacity-50"
                        checked={walletRedeemAllowed && redeemWallet}
                        disabled={!walletRedeemAllowed}
                        onChange={(e) => walletRedeemAllowed && setRedeemWallet(e.target.checked)}
                    />
                    <span>
                        <span className="font-medium text-slate-900">Redeem channel wallet</span>
                        <span className="mt-0.5 block text-sm text-slate-600">
                            The partner wallet will be charged for the billed amount, and the order will be
                            prepaid. The delivery partner will not collect cash.
                        </span>
                        {redeemWalletStatusText && (
                            <span className="mt-2 block text-sm text-slate-500">{redeemWalletStatusText}</span>
                        )}
                    </span>
                </label>
            </div>

            <div>
                <div className="flex justify-start space-x-4 mt-6">
                    <button 
                        onClick={async () => {
                            try {
                                const saveSuccess = await handleSave();
                                if (saveSuccess) {
                                    setIsLoading(true);
                                    await onBill({ redeemWallet: walletRedeemAllowed && redeemWallet });
                                    toast.success('Order created. Opening conversation…');
                                }
                            } catch (error) {
                                console.error('Error billing:', error);
                                const msg = getApiErrorMessage(error, 'Billing failed.');
                                toast.error(msg);
                            } finally {
                                setIsLoading(false);
                            }
                        }} 
                        className="btn-secondary flex items-center space-x-2"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-primary-600" />
                        ) : (
                            <i className="material-icons-outlined text-base">receipt_long</i>
                        )}
                        <span>Bill</span>
                    </button>
                </div>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onCancel} className="btn-secondary flex items-center space-x-2" disabled={isLoading}>
                        <i className="material-icons-outlined text-base">close</i>
                        <span>Cancel</span>
                    </button>
                    <button
                        onClick={async ()=>{
                            const ok = await handleSave();
                            if (ok) window.location.reload();
                        }}
                        className="btn-primary flex items-center space-x-2"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                            <i className="material-icons-outlined text-base">save</i>
                        )}
                        <span>Save Changes</span>
                    </button>
                </div>
            </div>

            {showBatchModal && (
                <BatchSelectionModal
                    batches={batches}
                    onSelect={handleBatchSelect}
                    onClose={() => setShowBatchModal(false)}
                    onAddShortbook={async () => {
                        const ok = await handleSave();
                        if (!ok) return;
                        setShowBatchModal(false);
                        setShortbookStrip('');
                        setShortbookModalOpen(true);
                    }}
                />
            )}

            <Modal
                isOpen={shortbookModalOpen}
                onClose={() => {
                    if (!shortbookSubmitting) setShortbookModalOpen(false);
                }}
                title="Add to shortbook"
                description="Enter how many strips to add to the pharmacy shortbook for this medicine."
            >
                <form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        const q = Number(shortbookStrip);
                        if (!shortbookStrip.trim() || Number.isNaN(q) || q < 1) {
                            toast.error('Enter a valid strip quantity (1 or more).');
                            return;
                        }
                        setShortbookSubmitting(true);
                        try {
                            await onAddShortbook(medicine.medicine_id, draft.order_id, q);
                            toast.success('Added to shortbook.');
                            setShortbookModalOpen(false);
                        } catch (err) {
                            toast.error(getApiErrorMessage(err, 'Failed to add to shortbook.'));
                        } finally {
                            setShortbookSubmitting(false);
                        }
                    }}
                    className="space-y-4"
                >
                    <div>
                        <label htmlFor="shortbook-strip-qty" className="mb-1.5 block text-sm font-medium text-slate-700">
                            Strip quantity
                        </label>
                        <input
                            id="shortbook-strip-qty"
                            type="number"
                            min={1}
                            step={1}
                            className="input-field"
                            value={shortbookStrip}
                            onChange={(ev) => setShortbookStrip(ev.target.value)}
                            disabled={shortbookSubmitting}
                            autoFocus
                        />
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-5">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setShortbookModalOpen(false)}
                            disabled={shortbookSubmitting}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary min-w-[7rem]" disabled={shortbookSubmitting}>
                            {shortbookSubmitting ? (
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Adding…
                                </span>
                            ) : (
                                'Add to shortbook'
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {showManualEntryModal &&
                <ManualMedicineEntry onSave={handleManualMedicineSave} onClose={() => setShowManualEntryModal(false)}/>}
        </div>
    );
}

export default DraftEditor; 